/**
 * DXF 导出配置的不可变类型基础（无运行时依赖）。
 */

export enum DxfLayerRole {
  BigPlank = 'BigPlank',
  Parts = 'Parts',
  HolesFront = 'HolesFront',
  HolesBack = 'HolesBack',
  SlotsFront = 'SlotsFront',
  SlotsBack = 'SlotsBack',
  CurveHolesFront = 'CurveHolesFront',
  CurveHolesBack = 'CurveHolesBack',
  MillInfoFront = 'MillInfoFront',
  MillInfoBack = 'MillInfoBack',
  PartText = 'PartText',
  BigPlankText = 'BigPlankText',
  OverflowText = 'OverflowText',
  OverflowLine = 'OverflowLine',
}

export const ALL_LAYER_ROLES: readonly DxfLayerRole[] = Object.freeze(
  Object.values(DxfLayerRole) as DxfLayerRole[]
)

export interface LayerDef {
  readonly name: string
  readonly color: number
  readonly lineType: string
}

export type LayerMap = Readonly<Record<DxfLayerRole, LayerDef>>

/* ------------------------------------------------------------------ */
/*  配置契约 — 受限的几何+序列化组合，注册时会校验                        */
/* ------------------------------------------------------------------ */

export const DxfProfileContract = {
  Planar2D: 'planar-2d',
  ThicknessOCS: 'thickness-ocs',
} as const
export type DxfProfileContract =
  (typeof DxfProfileContract)[keyof typeof DxfProfileContract]

/* ------------------------------------------------------------------ */
/*  配置                                                               */
/* ------------------------------------------------------------------ */

export type DxfProfileId = string

export interface DxfProfile {
  readonly id: DxfProfileId
  readonly label: string
  readonly layerMap: LayerMap
  readonly contract: DxfProfileContract
  /** 是否启用侧孔（side-drill）策略，仅 specialized-3d-v1 为 true。 */
  readonly sideDrill: boolean
  /** 是否在侧孔入射面输出 WCS LINE 展示圆（VIEW_SIDE_HOLE 层）。 */
  readonly sideDrillView: boolean
}

/* ------------------------------------------------------------------ */
/*  解析结果                                                           */
/* ------------------------------------------------------------------ */

export type ProfileResolution =
  | { readonly ok: true; readonly profile: DxfProfile }
  | {
      readonly ok: false
      readonly reason: 'invalid_profile_id'
      readonly id: string
    }

/* ------------------------------------------------------------------ */
/*  类型守卫                                                           */
/* ------------------------------------------------------------------ */

export function isDxfProfile(value: unknown): value is DxfProfile {
  if (value == null || typeof value !== 'object') return false
  const p = value as Record<string, unknown>
  return (
    typeof p.id === 'string' &&
    typeof p.label === 'string' &&
    typeof p.layerMap === 'object' &&
    p.layerMap != null &&
    typeof p.contract === 'string' &&
    typeof p.sideDrill === 'boolean' &&
    typeof p.sideDrillView === 'boolean'
  )
}

export function has3D(p: DxfProfile): boolean {
  return p.contract === DxfProfileContract.ThicknessOCS
}
